import { ContentLiteData } from 'src/types/consumption';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  NavMenuElements,
  PageTypes,
  SEO_CONTENT
} from 'src/utils/constants/stringConstants';
import { dateConversionObj, timeConversion } from 'src/utils/conversions';
import {
  getActorInfo,
  getFormattedLanguages
} from 'src/utils/getFormattedData';

import * as styles from './SeoContentStyles';
import { CELEBRITY } from 'src/utils/constants/CelebsConstants';
import { ContentTypes } from '../thumbnail/types';

type SeoContentType = {
  seoData: ContentLiteData | null;
  pageType: string;
  header: string;
  sportsData?: DynamicObject;
  popularData?: DynamicObject;
  latestData?: DynamicObject;
  actor?: string;
  showOrWebSeries?: string;
  totalEpisodes?: string;
};

const SeoContent = ({
  seoData,
  pageType,
  header,
  sportsData,
  popularData,
  latestData,
  actor,
  showOrWebSeries,
  totalEpisodes
}: SeoContentType) => {
  const {
    actors,
    directors,
    duration,
    genres,
    genre,
    original_title,
    release_date,
    releaseDate,
    languages
  } = seoData ?? {
    actors: [],
    directors: [],
    duration: 0,
    genres: [],
    genre: [],
    original_title: '',
    release_date: '',
    languages: []
  };

  const {
    tournamentName,
    startDate,
    endDate,
    venue,
    format,
    firstEdition,
    latestEdition,
    nextEdition,
    noOfTeams
  } = sportsData || {};

  const getReleaseDate = (releaseDate: string | Date) => {
    const formattedDate = dateConversionObj(releaseDate);
    let date;
    if (formattedDate) {
      date = `${formattedDate.day} ${formattedDate.month} ${formattedDate.year}`;
    }
    return String(date);
  };
  const actorsInfo = actors && actors.length && getActorInfo(actors);
  let formattedActorsData = actorsInfo
    ? actorsInfo.map((item: string[]) => item[0])
    : [];
  formattedActorsData =
    formattedActorsData?.length >= 5
      ? formattedActorsData.slice(0, 5)
      : formattedActorsData;
  let releaseDateTitle = '';
  if (totalEpisodes || pageType === PageTypes.EPISODE_CONSUMPTION) {
    releaseDateTitle =
      pageType === PageTypes.SHOW_CONSUMPTION
        ? `${showOrWebSeries} Released Date`
        : `Release Date`;
  } else {
    releaseDateTitle = 'Movie Released Date';
  }

  const tableKeyValue = (key: string, valueText: string) => {
    return (
      <tr>
        <td css={styles.keyValues}>
          <strong>{key}</strong>
        </td>
        <td>
          <div css={styles.value}>{valueText}</div>
        </td>
      </tr>
    );
  };

  const actorAndDirector = (key: string, valueText: string[]) => {
    return (
      <tr>
        <td css={styles.keyValues}>
          <strong>{key}</strong>
        </td>
        <td>
          <ul css={styles.valueWrap}>
            {valueText.map((item: string) => {
              return (
                <li key={item} css={styles.value}>
                  {item}
                </li>
              );
            })}
          </ul>
        </td>
      </tr>
    );
  };

  const getGenres = (genres: DynamicObject[]) => {
    return (
      <ul css={styles.valueWrap}>
        {genres?.map((genreName: DynamicObject) => {
          return (
            <li key={genreName.id} css={styles.value}>
              {genreName.value}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      {header ? (
        <div css={styles.seoTableContent(pageType)}>
          <h2 css={styles.headerStyle}>{header}</h2>
          {pageType &&
          pageType !== PageTypes.MOVIE_CONSUMPTION &&
          latestData &&
          latestData.length >= 2 &&
          latestData.length <= 5 ? (
            <>
              <div itemScope itemType="https://schema.org/Table ">
                <table css={styles.movieTable(pageType)}>
                  <thead>
                    <tr>
                      <th>
                        {!(pageType === 'celebs')
                          ? SEO_CONTENT.NAME
                          : CELEBRITY.MOVIE_NAME}
                      </th>
                      <th>
                        {pageType === PageTypes.SHOWS
                          ? 'Language'
                          : 'Released Date'}
                      </th>
                      <th>{SEO_CONTENT.GENRE}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestData.map((keys: DynamicObject, index: number) => {
                      let tdText = '';
                      if (pageType === PageTypes.SHOWS) {
                        tdText =
                          keys.languages &&
                          keys.languages.length &&
                          getFormattedLanguages(languages);
                      } else {
                        tdText = getReleaseDate(
                          keys.release_date || keys.releaseDate
                        );
                      }
                      return (
                        <>
                          <tr data-testid={`seoLatest-${index}`}>
                            <td>
                              <span>{keys.title}</span>
                            </td>
                            <td>{tdText}</td>
                            <td>
                              {(keys.genres &&
                                keys.genres.length &&
                                keys.genres[0].value) ||
                                (keys.genre &&
                                  keys.genre.length &&
                                  keys.genre[0].value)}
                            </td>
                          </tr>
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {popularData &&
              popularData.length >= 2 &&
              popularData.length <= 5 ? (
                <div
                  itemScope
                  itemType="https://schema.org/ItemList "
                  css={styles.itemList(pageType)}>
                  <h2 itemProp="name" css={styles.headerStyle}>
                    {pageType === PageTypes.CELEB
                      ? `List of Top ${actor} Movies :`
                      : `Top 5 popular ${pageType
                          .toString()
                          .toLowerCase()} to watch :`}
                  </h2>

                  <link
                    itemProp="itemListOrder"
                    href="https://schema.org/ItemListOrderDescending "
                  />
                  {popularData.map((key: DynamicObject, item: number) => (
                    <p key={item} css={styles.popularList}>
                      {item + 1}.{' '}
                      <span
                        itemProp="itemListElement"
                        data-testid={`seoGenres-${item}`}>
                        {key.title}
                      </span>
                    </p>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
          {pageType === PageTypes.CONSUMPTION ||
          pageType === PageTypes.SHOW_CONSUMPTION ||
          pageType === PageTypes.TVSHOWDETAILS ||
          pageType === PageTypes.MOVIE_CONSUMPTION ||
          pageType === PageTypes.EPISODE_CONSUMPTION ? (
            <>
              <div>
                <table css={styles.movieTable(pageType)}>
                  <tbody>
                    {(release_date || releaseDate) &&
                      tableKeyValue(
                        releaseDateTitle,
                        getReleaseDate(release_date || releaseDate || '')
                      )}
                    <tr>
                      <td css={styles.keyValues}>
                        <strong>
                          {totalEpisodes ? 'Total Episodes' : 'Genres'}
                        </strong>
                      </td>
                      <td>
                        {totalEpisodes ? (
                          <div css={styles.value}>{totalEpisodes}</div>
                        ) : (
                          getGenres(genres)
                        )}
                      </td>
                    </tr>
                    {languages && languages.length
                      ? actorAndDirector(
                          SEO_CONTENT.AUDIO_LANGUAGES,
                          getFormattedLanguages(languages)
                        )
                      : null}
                    {formattedActorsData && formattedActorsData.length
                      ? actorAndDirector(SEO_CONTENT.CAST, formattedActorsData)
                      : null}
                    {directors && directors.length
                      ? actorAndDirector(SEO_CONTENT.DIRECTORS, directors)
                      : null}

                    {totalEpisodes ? (
                      <tr>
                        <td className="keyValues">
                          <strong>{SEO_CONTENT.GENRES}</strong>
                        </td>
                        <td>{getGenres(genres || genre)}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {totalEpisodes ||
              pageType === PageTypes.SHOW_CONSUMPTION ||
              pageType === PageTypes.TVSHOWDETAILS ||
              pageType === PageTypes.EPISODE_CONSUMPTION ? null : (
                <div
                  itemScope
                  itemType="https://schema.org/ItemList "
                  css={styles.consumptiondataWrapper}>
                  {original_title && (
                    <h2 itemProp="name" css={styles.headerStyle}>
                      {`${SEO_CONTENT.KEY_POINTS} ${original_title}:`}
                    </h2>
                  )}
                  <link
                    itemProp="itemListOrder"
                    href="https://schema.org/ItemListOrderDescending "
                  />
                  <p css={styles.popularList}>
                    1.{' '}
                    <span itemProp="itemListElement">
                      {(pageType === PageTypes.MOVIE_CONSUMPTION && duration > 0
                        ? SEO_CONTENT.MOVIE_DURATION
                        : SEO_CONTENT.EPISODE_DURATION) +
                        timeConversion(duration)}
                    </span>
                  </p>
                  <p css={styles.popularList}>
                    2.{' '}
                    <span itemProp="itemListElement">
                      {(languages?.length > 1
                        ? SEO_CONTENT.AUDIO_LANGUAGES
                        : SEO_CONTENT.AUDIO_LANGUAGE) +
                        getFormattedLanguages(languages)}
                    </span>
                  </p>
                </div>
              )}
            </>
          ) : null}
          {pageType === PageTypes.SPORTS_LANDING && sportsData && (
            <>
              <table css={styles.movieTable(pageType)}>
                <tbody>
                  {tournamentName &&
                    tableKeyValue(SEO_CONTENT.TOURNAMENT_NAME, tournamentName)}
                  {startDate &&
                    tableKeyValue(SEO_CONTENT.START_DATE, startDate)}
                  {endDate && tableKeyValue(SEO_CONTENT.END_DATE, endDate)}
                  {venue && tableKeyValue(SEO_CONTENT.VENUE, venue)}
                  {format && tableKeyValue(SEO_CONTENT.FORMAT, format)}
                  {firstEdition &&
                    tableKeyValue(SEO_CONTENT.FIRST_EDITION, firstEdition)}
                  {latestEdition &&
                    tableKeyValue(SEO_CONTENT.LATEST_EDITION, latestEdition)}
                  {nextEdition &&
                    tableKeyValue(SEO_CONTENT.NEXT_EDITION, nextEdition)}
                  {noOfTeams && tableKeyValue(SEO_CONTENT.NO_TEAMS, noOfTeams)}
                </tbody>
              </table>
            </>
          )}
        </div>
      ) : null}
    </>
  );
};

export default SeoContent;
